
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { VueMaskFilter } from 'v-mask'

// Vue
import {
	ComponentPublicInstance,
	computed,
	ComputedRef,
	defineComponent,
	nextTick,
	onBeforeMount,
	onBeforeUnmount,
	onMounted,
	reactive,
	ref,
	watch
} from 'vue'

// Store
import { createVuexHelpers } from 'vue2-helpers'

// Components
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import IconSvg from '@/components/IconSvg.vue'
import TInput from '@/components/Ticketing/Controls/TInput.vue'
import TPhoneInput from '@/components/Ticketing/Controls/TPhoneInput.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import Loader from '@/components/Loaders/Loader.vue'
import LoaderWithTitle from '@/components/Loaders/LoaderWithTitle.vue'
import TTimer from '@/components/Ticketing/TTimer.vue'
import TCodeInputsGroup from '@/components/Ticketing/Controls/TCodeInputsGroup.vue'
import TicketingPaymentFooter from '@/viewsTicketing/Events/components/TicketingPaymentFooter.vue'
import TicketingInfoModal from '@/components/modals/Ticketing/TicketingInfoModal.vue'
import EventDetailsText from '@/viewsTicketing/Events/components/EventDetailsText.vue'
import EventPeriod from '@/components/Ticketing/EventPeriod.vue'
import EventDateIcon from '@/components/Ticketing/EventDateIcon.vue'
import FormattedPrice from '@/components/FormattedPrice.vue'
import TicketingPaymentMethod from '@/components/Ticketing/TicketingPaymentMethod.vue'
import CreditCardModal from '@/components/modals/CreditCardModal.vue'
import PurchaseLimitHint from '@/components/Ticketing/PurchaseLimitHint.vue'
import EventTiers from '@/viewsTicketing/Events/components/EventTiers.vue'

// Constants
import {
  DEFAULT_SWIPER_OPTIONS,
  TIME_FORMATS
} from '@/constants'
const FORM_FIELDS = ['firstName', 'lastName', 'phone', 'email'] as PropName[]
const DEFAULT_HEADER_HEIGHT = 130
const defaultErrorState = {
	firstName: '',
	lastName: '',
	phone: '',
	email: ''
}

const UNLIMITED_COUNT = 1000000

// Types
import { EventStatus, EventCategory, PaymentProvider, PaymentTypes } from '@/types/constantsTypes'
import {
	CreditCard,
	EventImage,
	EventTicketType,
	FormattedEventEntity,
	LocalEventTicket,
	TicketOrder
} from '@/types/entitiesTypes'
import { paymentActions, VenueInfoState } from '@/types/storeTypes'
import { CreateTicketOrderPayload, EventAvailableTicketsData } from '@/types/apiTypes'
import { CatchError, PhoneCode } from '@/types/commonTypes'
import { InputsType, PaymentFooterType, CodeInputsType } from '@/types/componentsTypes'
import { AxiosError } from 'axios'
enum FormRequiredErrors {
	firstName = 'First name required',
	lastName = 'Last name required',
	phone = 'Verified phone number required',
	email = 'Email required'
}
type PropName = 'firstName' | 'lastName' | 'phone' | 'email'

type FormFieldsRef = {
	firstName: InputsType | null,
	lastName: InputsType | null,
	phone: InputsType | null,
	email: InputsType | null,
}

// Icons
import BackIcon from '@/assets/images/ic_arrow_back.svg'

// Api
import eventApi from '@/api/events'

// Utils
import {
	getFormattedTime,
	emailValidation,
} from '@/utils/helpers'
import { getEventPeriod, getEventStartDateInfo } from '@/utils/dateUtils'
import storage from '@/utils/storage.js'
const { getStorage, setStorage } = storage
import { isNumber } from 'lodash'

// Styles
import 'swiper/css/swiper.css'

// Router
import { useRouter, type Router, useRoute } from 'vue2-helpers/vue-router'
import { Route } from 'vue-router'

// Composable
import { useMobile } from '@/utils/composable/useMobile'

export default defineComponent({
	name: 'EventDetails',

	components: {
		EventTiers,
		PurchaseLimitHint,
		CreditCardModal,
		TicketingPaymentMethod,
		FormattedPrice,
		EventDateIcon,
    EventPeriod,
    EventDetailsText,
    TicketingInfoModal,
    TicketingPaymentFooter,
		Swiper,
		SwiperSlide,
		IconSvg,
		TInput,
		TPhoneInput,
    PrimaryButton,
		Loader,
		LoaderWithTitle,
		TTimer,
		TCodeInputsGroup
	},

	setup() {
		// Router
		const router: Router = useRouter()
		const route: Route = useRoute()

		// Vuex
		const { useState, useActions, useGetters } = createVuexHelpers()
		const { venueInfo }: VenueInfoState = useState('venue', ['venueInfo'])
    const { caPaymentProvider, timeZoneName }: {
      caPaymentProvider: ComputedRef<number>,
      timeZoneName: ComputedRef<string>
    } = useGetters('venue', ['caPaymentProvider', 'timeZoneName'])
    const { mountTEconnect }: paymentActions = useActions('payment', ['mountTEconnect'])
		const { id: userId }: { id: ComputedRef<string> } = useState('user', ['id'])
		const { isMacOrIOS }: { isMacOrIOS: ComputedRef<boolean> } = useState('ui', ['isMacOrIOS'])
		const { isMobileScreen } = useState('ui', ['isMobileScreen'])
		const {
			defaultCreditCard
		}: { defaultCreditCard: ComputedRef<CreditCard> } = useGetters('payment', [
			'defaultCreditCard'
		])

		// Reactive
		const state = reactive({
			currentEvent: {} as FormattedEventEntity,
			form: {
				firstName: '',
				lastName: '',
				phone: '',
				email: ''
			},
			errors: defaultErrorState,
      ticketOrder: {} as TicketOrder,
			eventAvailableTickets: {} as EventAvailableTicketsData,
			localTicketTypes: [] as LocalEventTicket[]
		})

    // Timeouts
    let scrollTimeout = 0
    let scrollLimitTimeout = 0

		// Refs
		const loading = ref(false)
		const codeInputs = ref<CodeInputsType | null>(null)
		const limitHint = ref<ComponentPublicInstance | null>(null)
		const paymentFooter = ref<PaymentFooterType | null>(null)
		const isTimerShown = ref(false)
		const isScrollingToLimitHint = ref(false)
		const isPhoneSent = ref(false)
		const isCodeSending = ref(false)
		const codeError = ref('')
		const isPhoneVerificationLoading = ref(false)
		const isEventNotActive = ref(false)
		const isScrollBellowSwiper = ref(false)
		const checkingRequestSent = ref(false)
    const localPhoneValue = ref('')
    const isPhoneVerificationVisible = ref(true)
		const formFieldsRef = ref<FormFieldsRef>({
			firstName: null,
			lastName: null,
			phone: null,
			email: null,
		})
		const isEmptyField = ref(false)
		const isShowCreditCardModal = ref(false)

		// Computed
		const form = computed(() => state.form)
    const isFormDisabled = computed(() => isEventNotActive.value)
		const errors = computed(() => state.errors)
		const isTimerVisible = computed(
			() => isPhoneSent.value && isTimerShown.value
		)
		const backIcon = computed(() => BackIcon)
		const purchaseLimitRemaining = computed(() => state.eventAvailableTickets.purchaseLimitRemaining)
		const purchaseLimit = computed(() => state.eventAvailableTickets.purchaseLimit || 0)
		const isOverLimitForAccount = computed(
			() => purchaseLimitRemaining.value !== null &&
				orderSumData.value.ticketsAmount > purchaseLimitRemaining.value
		)
		const allTicketsBought = computed(
			() =>
			isNumber(purchaseLimitRemaining.value) && purchaseLimitRemaining.value === 0
		)
		const isLimitHintShown = computed(() => allTicketsBought.value || isOverLimitForAccount.value)
		const currentEvent = computed(() => state.currentEvent)
		const isEventDraft = computed(() => currentEvent.value.status === EventStatus.DRAFT)
		const category = computed(() => EventCategory[currentEvent.value.categoryId])
    const orderId = computed(() => state.ticketOrder.id)
    const ticketOrder = computed(() => state.ticketOrder)
    const ticketOrderCreditCardId = computed(() => state.ticketOrder.creditCardId)
    const localTicketTypes = computed(() => state.localTicketTypes)
		const isOpenAdmissionEvent = computed(() => !state.localTicketTypes.length)
		const orderSumData = computed(() =>
			localTicketTypes.value.reduce((data: { ticketsAmount: number, amount: number }, item) => {
				return {
					ticketsAmount: data.ticketsAmount + Number((item as LocalEventTicket)?.quantity),
					amount: data.amount + Number((item as LocalEventTicket)?.quantity) * Number(item.ticketPrice)
				}
			}, { ticketsAmount: 0, amount: 0 })
		)
		const isPurchaseButtonDisabled = computed(
			() =>
				isFormDisabled.value ||
				orderSumData.value.ticketsAmount === 0 ||
				isOverLimitForAccount.value
		)
		const isZeroPriceTicket = computed(() => localTicketTypes.value.find(type => Number(type.ticketPrice) === 0))
		const isNotZeroPriceTicket = computed(() => localTicketTypes.value.find(type => Number(type.ticketPrice) !== 0))
		const isPhoneVerified = computed(
			() =>
          !!(ticketOrder.value.phoneConfirmed && localPhoneValue.value === ticketOrder.value.phone)
		)
		const creditCardData = computed(() => ticketOrder.value.creditCardId && defaultCreditCard.value || {})

    const isPhoneVerificationDisabled = computed(() => isTimerVisible.value || isPhoneVerified.value)
		const phoneInfoMessage = computed(() => {
			if (isPhoneVerified.value) {
				return 'Verified'
			}

			if (isPhoneVerificationDisabled.value && !isPhoneVerificationVisible.value) {
				return 'Phone verification is available every 60 seconds'
			}

			return ''
		})

		// Watchers
		watch(ticketOrder, (nv, ov) => {
			if (nv.phoneConfirmed && !ov.phoneConfirmed) isPhoneSent.value = false
		})

		watch(isLimitHintShown, (nv) => {
			if (nv) {
				scrollToLimitHint()
			}
		})

		// Hooks
		onBeforeMount(async () => {
      if (caPaymentProvider.value === PaymentProvider.MAGENSA) {
        mountTEconnect()
      }

      try {
        loading.value = true
        await Promise.all([getEvent(), getOrder()])
				state.localTicketTypes = currentEvent.value.eventTicketTypes.map(tier => ({
					...tier,
					maxCount: isNumber(tier.ticketLeft) ? tier.ticketLeft : UNLIMITED_COUNT,
					quantity: 0,
					// TODO: Fix TS error
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					soldOut: tier.ticketLeft === 0,
				}))
				updateLocalEventTiers()
				applyInitialSingleTierPrefill()
				await getEventAvailableTickets()
      } finally {
        loading.value = false
      }
		})

		onMounted(() => {
      addListeners()
      const timer = getTimer()

      if (timer && timer > 0) {
        isPhoneSent.value = true
        isTimerShown.value = true

        scrollAndFocusCodeInput(1000)
      }
    })

		onBeforeUnmount(() => {
      removeListener()

      const timer = getTimer()

      if (timer) {
        setStorage('resentTimer', '0')
      }
    })

		// Methods
		const updateLocalEventTiers = (): void => {
			if (!state.ticketOrder.ticketOrderItems || !state.localTicketTypes) return

			state.localTicketTypes = state.localTicketTypes.map(type => {
				const orderType = state.ticketOrder.ticketOrderItems.find(item => item.eventTicketType.id === type.id)

				if (!orderType) return type

				return {
					...type,
					orderItemId: orderType.id,
					quantity: orderType.quantity,
				}
			})
		}
		const isSingleTierActiveEvent = (): boolean =>
			state.localTicketTypes.length === 1 &&
			currentEvent.value.status === EventStatus.ACTIVE

		const hasSingleTierExistingSelection = (): boolean => {
			if (!isSingleTierActiveEvent()) return false

			const [singleTier] = state.localTicketTypes

			return Boolean(singleTier?.orderItemId) || Number(singleTier?.quantity) > 0
		}

		const isSingleTierUnavailable = (): boolean => {
			if (!isSingleTierActiveEvent()) return false

			const [singleTier] = state.localTicketTypes

			if (!singleTier) return false

			const isTierUnavailable = Boolean(singleTier.soldOut) || singleTier.maxCount <= 0
			const isPurchaseLimitReached =
				isNumber(purchaseLimitRemaining.value) && purchaseLimitRemaining.value === 0

			return isTierUnavailable || isPurchaseLimitReached
		}

		const updateSingleTierQuantity = (quantity: number): void => {
			const [singleTier] = state.localTicketTypes

			if (!singleTier) return

			state.localTicketTypes = [
				{
					...singleTier,
					quantity
				}
			]
		}

		const applyInitialSingleTierPrefill = (): void => {
			if (!isSingleTierActiveEvent() || hasSingleTierExistingSelection()) return

			updateSingleTierQuantity(isSingleTierUnavailable() ? 0 : 1)
		}

		const syncSingleTierWithAvailability = (): void => {
			if (!isSingleTierActiveEvent()) return

			if (isSingleTierUnavailable()) {
				updateSingleTierQuantity(0)
			}
		}

		const getEventAvailableTickets = async (): Promise<void> => {
			const phone = getPhoneForRequest(state.form.phone)

			if (!phone) {
				syncSingleTierWithAvailability()

				return
			}

			try {
				state.eventAvailableTickets = (await eventApi.getEventAvailableTickets(phone))?.data || {}
			} catch (e) {
			} finally {
				syncSingleTierWithAvailability()
			}
		}
    const getTimer = (): number => {
      return Number(getStorage('resentTimer'))
    }

    const addListeners = (): void => {
      window.addEventListener("scroll", checkScrollOffset);
    }

    const checkScrollOffset = () => {
      const swiperHeightWithOffset = 240
      if (window?.scrollY) {
        const scrollY = window.scrollY
        if (scrollY > swiperHeightWithOffset && !isScrollBellowSwiper.value) {
          isScrollBellowSwiper.value = true
        }
        if (scrollY < swiperHeightWithOffset && isScrollBellowSwiper.value) {
          isScrollBellowSwiper.value = false
        }
      }
    }

    const handlePay = (paymentType: string): void => {
			FORM_FIELDS.every(fieldName => {
				validateProp(fieldName, true)
				if (isEmptyField.value) {
					scrollToFirstEmptyField(fieldName)
					return false
				}
				return true
			})

			if (errors.value.firstName || errors.value.email || isEmptyField.value) return

			if (!isPhoneVerified.value) return

      if (!paymentType) paymentFooter?.value?.openCreditCardModal()

      if (paymentType === PaymentTypes.reserveAndPurchase) {
				paymentFooter?.value?.payWithoutMagensaModal()
			}
    }

    const getMaskedPhone = (phone: string): string => {
      if (!phone) return ''

      let formattedPhone = phone.replace('+1', '').replace('+', '')

      if (formattedPhone?.length === 12) {
        formattedPhone = VueMaskFilter(formattedPhone, '+### ## ###-##-##')

        return formattedPhone

      }

      if (formattedPhone?.length === 10) {
        formattedPhone = VueMaskFilter(formattedPhone, '(###) ###-####')

        return formattedPhone
      }

      if (formattedPhone?.length === 11) {
        formattedPhone = VueMaskFilter(formattedPhone, '+## ### ### ###')

        return formattedPhone
      }

      throw new Error('Unrecognized phone format');
    }

    const changeTicketOrder = (ticketOrder: TicketOrder): void => {
      state.ticketOrder = ticketOrder
      localPhoneValue.value = ticketOrder.phone || ''
    }

    const getOrder = async (withTiersUpdating = false): Promise<void> => {
      const { eventId } = route.params


      try {
        state.ticketOrder = (await eventApi.getEventOrder(eventId))?.data || {}
        const phone = state?.ticketOrder?.phone || ''
        let maskedPhone = getMaskedPhone(phone)

        state.form = {
          email: state.ticketOrder.email || '',
          phone: maskedPhone,
          lastName: state.ticketOrder.lastName || '',
          firstName: state.ticketOrder.firstName || '',
        }

        localPhoneValue.value = state.ticketOrder.phone || ''

        if (isPhoneVerified.value) {
          isPhoneSent.value = false
          isPhoneVerificationVisible.value = true
        }

				if (withTiersUpdating) {
					updateLocalEventTiers()
				}
      } catch (e) {
				// TODO: Remove from the 4.2
				const error = e as CatchError

				if (error instanceof AxiosError ) {
					if (error?.response?.data?.message === 'Event status is not active') {
						await showUnavailablePopup()
					}
				}
      }
    }

    const getEvent = async(): Promise<void> => {
      const { eventId } = route.params

      try {
        const data = (await eventApi.getEventById(eventId))?.data

        if (data) {
					if (data.status !== EventStatus.ACTIVE && data.status !== EventStatus.DRAFT) {
						await showUnavailablePopup()

						return
					}

          const from = `${data.startDate} ${data.startTime}` as string
          const to = `${data.endDate} ${data.endTime}` as string

          state.currentEvent = {
            ...data,
            entryTime: getFormattedTime(data.entryTime, null, TIME_FORMATS.AMPM),
            eventImages: data.eventImages.sort((a: EventImage, b: EventImage) => a.position - b.position),
            period: getEventPeriod({ from, to }),
						eventStartDateInfo: getEventStartDateInfo(
							from,
							data.entryTime
						),
						eventTicketTypes: data.eventTicketTypes
							.sort((a: EventTicketType, b: EventTicketType) => a.groupPosition - b.groupPosition),
          }
        }
      } catch (e) {
        await showUnavailablePopup()
      }
    }

		const sendCode = async (code: PhoneCode): Promise<void> => {
			codeError.value = ''
			isCodeSending.value = true
			const payload = {
				phoneCodeFromUser: code,
				ticketOrderId: orderId.value
			}

			try {
				const { phoneConfirmed } = (await eventApi.sendPhoneCode(payload))?.data

        if (phoneConfirmed) {
          await getOrder()
					state.errors.phone = ''
        }

				isPhoneSent.value = false
				window.localStorage.removeItem('resentTimer')
			} catch (e) {
				codeError.value = 'Wrong code'
			} finally {
				isCodeSending.value = false
			}
		}

		const hideTimer = (): void => {
			isTimerShown.value = false
		}

    const scrollAndFocusCodeInput = (time: number): void => {
      if (scrollTimeout) clearTimeout(scrollTimeout)

      scrollTimeout = setTimeout(() => {
        // Wait scrolling end
				if (isScrollingToLimitHint.value) return

        if (addEventListener) {
          addEventListener(
              "scrollend",
              () => codeInputs?.value?.focusFirstField(),
              { once: true }
          );
        }

        const codeInputsEl = codeInputs?.value?.$el

        if (codeInputsEl) {
					const y = codeInputsEl.getBoundingClientRect().top + window.scrollY - getHeaderHeight();

          window.scrollTo({top: y, behavior: 'smooth'});
        }

      }, time)
    }

    const switchVerificationVisibility = () => {
			isPhoneVerificationVisible.value = true
    }

    const sendVerification = async(): Promise<void> => {
      await nextTick(async () => {
        validateProp('phone')

        if (!state.errors.phone) {
            const phone = getFormattedPhone(form.value.phone)
            const formattedPhone = phone.length === 10 ? `+1${phone}` : `+${phone}`

            if (formattedPhone === ticketOrder.value.phone && ticketOrder.value.phoneConfirmed) {
              return
            }

            const payload = {
              phone: formattedPhone,
              ticketOrderId: orderId.value
            }

            try {
              isPhoneVerificationLoading.value = true
              await eventApi.verifyPhone(payload)
              isPhoneSent.value = true
              isTimerShown.value = true
              switchVerificationVisibility()
							nextTick(() => {
								codeError.value = ''
								codeInputs.value?.resetCode()
							})
              setStorage('resentTimer', 60)

              scrollAndFocusCodeInput(700)
            } catch (e) {
              console.log(e)
            } finally {
              isPhoneVerificationLoading.value = false
            }
        }
      })
    }

		const verify = async (): Promise<void> => {
      if (isPhoneVerified.value) return

			await sendVerification()
		}

		const sendOrderRequest = async (
			payload: CreateTicketOrderPayload,
			prop: PropName,
		): Promise<void> => {
      try {
        state.ticketOrder =
            (await eventApi.updateEventOrder({ ...payload, orderId: orderId.value }))?.data || {}

        localPhoneValue.value = state.ticketOrder.phone || ''

				if (prop === 'phone') {
					// Await is not necessary
					getEventAvailableTickets()
				}
      } catch (e) {}
		}

    const showUnavailablePopup = async (): Promise<void> => {
      isEventNotActive.value = true
      await back(true)
    }

    const formLocalValueChanged = (value: string) => {
        localPhoneValue.value = getPhoneForRequest(value)
    }

    const getPhoneForRequest = (phone: string): string => {
      if (!phone) return ''

      const formattedPhone = getFormattedPhone(phone)
      return formattedPhone.length === 10 ? `+1${formattedPhone}` : `+${formattedPhone}`
    }

		const handleFormChanged = (value: string, prop: PropName): void => {
			const trimmedValue = value?.trim() || ''
			const trimmedStateValue = state.form[prop]?.trim() || ''

      if (
				!value ||
				(trimmedStateValue && trimmedValue && trimmedStateValue === trimmedValue)
			) {
				return
			}

			state.form[prop] = trimmedValue
			validateProp(prop)

			if (!state.errors[prop]) {
				let phone = ''

				if (prop === 'phone') {
          phone = getPhoneForRequest(state.form.phone)

          if (phone === ticketOrder.value.phone) return
				}

        if (prop === 'email' && trimmedValue === ticketOrder.value.email) return

				const payload = {
					eventId: currentEvent.value.id,
					userId: userId.value,
					[prop]:
						prop !== 'phone'
							? state.form[prop]
							: phone
				}

				sendOrderRequest(payload, prop)
			}
		}

		const getHeaderHeight = (): number => {
			if (document) {
				const venueInfoHeader = document.querySelector('.event-details__venue-info') as HTMLElement
				const venueTitleHeader = document.querySelector('.event-details__title') as HTMLElement
				return (venueInfoHeader?.offsetHeight + venueTitleHeader?.offsetHeight) || DEFAULT_HEADER_HEIGHT
			}
			return DEFAULT_HEADER_HEIGHT
		}

		const scrollToLimitHint = () => {
			const limitHintEl = limitHint.value

			if (!limitHintEl) return

			isScrollingToLimitHint.value = true

			const windowScrollY = window.scrollY

			let yOffset = 10

			if (!isMobile) {
				yOffset = -5
			} else if (!isMacOrIOS.value) {
				yOffset = isKeyboardOpened.value ? 150 : 10
			}


			nextTick(() => {
				const y = limitHintEl.$el.getBoundingClientRect().top + windowScrollY - (getHeaderHeight() + yOffset);
				window.scrollTo({ top: y, behavior: 'smooth' });
				scrollLimitTimeout = setTimeout(() => isScrollingToLimitHint.value = false, 3000)
			})

		}

		const scrollToFirstEmptyField = (fieldName: PropName): void => {
			const field = formFieldsRef.value[fieldName]
			if (!field) return

			if (isMacOrIOS.value) {
				field.focusInput()
			} else {
				const windowScrollY = window.scrollY
				const y = field.$el.getBoundingClientRect().top + windowScrollY - getHeaderHeight();
				const isBottom = ((window.innerHeight + window.scrollY) >= document.body.scrollHeight)
				const isScrollNeeded = y !== windowScrollY && !isBottom

				if (!isScrollNeeded) {
					field.focusInput()
				} else {
					if (addEventListener) {
						addEventListener(
							"scrollend",
							() => field.focusInput(),
							{ once: true }
						);
					}

					window.scrollTo({top: y, behavior: 'smooth'});
				}
			}
		}

    const scrollToFirstError = (): void => {
      nextTick(() => {
        const firstError = document.getElementsByClassName('form__field__error')[0] as HTMLElement

        if (firstError) {
					const inputHeight = 50
					const y = firstError.getBoundingClientRect().top + window.scrollY - (getHeaderHeight() + inputHeight);

          window.scrollTo({top: y, behavior: 'smooth'});
        }
      })
    }

		const validateProp = (prop: PropName, isPurchaseClicked = false): void => {
			state.errors[prop] = ''

			if (isPurchaseClicked) {
				FORM_FIELDS.forEach(fieldName => {
					if (!form.value[fieldName]) {
						state.errors[fieldName] = `${FormRequiredErrors[fieldName]}`
					}
				})

				isEmptyField.value = false
			}

			if (!form.value[prop]) {
				if (isPurchaseClicked) {
					isEmptyField.value = true
				}

				return
			}

			if (prop === 'phone') {
				validatePhone()

				if (isPurchaseClicked && !state.errors.phone && !isPhoneVerified.value) {
					state.errors = {
						...state.errors,
						phone: 'Phone number has to be verified'
					}
				}
			}

			if (prop === 'email') {
				const isValid = emailValidation(form.value[prop])

				if (!isValid) state.errors.email = 'Check your email'
			}

			if (isPurchaseClicked) {
				scrollToFirstError()
			}
		}

		const removeListener = ():void => {
      window.removeEventListener("scroll", checkScrollOffset);

			if (scrollLimitTimeout) clearTimeout(scrollLimitTimeout)

			if (scrollTimeout) clearTimeout(scrollTimeout)
    }

		const validatePhone = (): void => {
			const phone = getFormattedPhone(form.value.phone)

			if (phone.length < 10 || phone.length > 12 || (phone.length !== 11 && phone[0] === '1')) {
				state.errors.phone = `Check your phone number`
			}
		}

		const getFormattedPhone = (phone: string): string => {
			return VueMaskFilter(phone, '#'.repeat(13))
		}

		const back = async (showUnavailableEventModal = false): Promise<void> => {
      const query = { ...route.query }

      if (showUnavailableEventModal) {
				query.showUnavailableEventModal = 'true'
				query.ticketsCount = `${orderSumData.value?.ticketsAmount || 0}`
			}

			await router.push({ name: 'events', query })
		}

		const onCleaned = (prop: PropName): void => {
			if (isPhoneSent.value && prop === 'phone') isPhoneSent.value = false

			state.form[prop] = ''
		}

		const openCreditCardModal = () :void => {
			isShowCreditCardModal.value = true
		}

		const hideCreditCardModal = (): void => {
			isShowCreditCardModal.value = false
		}

		const changeLocalTiers = (tiers: LocalEventTicket[]): void => {
			state.localTicketTypes = tiers
		}

		// Composable
		const { isKeyboardOpened, isMobile } = useMobile()

		return {
			back,
			handleFormChanged,
			verify,
			hideTimer,
			sendCode,
      getOrder,
      handlePay,
      changeTicketOrder,
      getEvent,
      formLocalValueChanged,
      showUnavailablePopup,
			onCleaned,
			openCreditCardModal,
			hideCreditCardModal,
			changeLocalTiers,
			getEventAvailableTickets,
			currentEvent,
			isPhoneSent,
			isCodeSending,
			loading,
			codeError,
			form,
			venueInfo,
			isMobileScreen,
			isPhoneVerified,
			errors,
			category,
			isTimerVisible,
			backIcon,
			DEFAULT_SWIPER_OPTIONS,
      ticketOrder,
      isPhoneVerificationLoading,
      codeInputs,
      paymentFooter,
      orderId,
      isFormDisabled,
      isScrollBellowSwiper,
      checkingRequestSent,
      isPhoneVerificationVisible,
      isPhoneVerificationDisabled,
      timeZoneName,
			phoneInfoMessage,
			creditCardData,
			formFieldsRef,
			isShowCreditCardModal,
			isPurchaseButtonDisabled,
			isZeroPriceTicket,
			isNotZeroPriceTicket,
			allTicketsBought,
			purchaseLimitRemaining,
			localTicketTypes,
			orderSumData,
			ticketOrderCreditCardId,
			isOverLimitForAccount,
			isOpenAdmissionEvent,
			purchaseLimit,
			limitHint,
			isEventDraft,
			isLimitHintShown
		}
	}
})
